/**
 * 동기화 페이지
 * Clean Efficiency: 토스 스타일의 깔끔한 동기화 관리
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSyncQueue } from "@/hooks/useSyncQueue";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { useSyncItemNames } from "@/hooks/useSyncItemNames";
import { ConflictDialog } from "@/components/ui/ConflictDialog";
import { SyncQueueItemCard } from "@/components/sync/SyncQueueItemCard";
import { SyncTimeline } from "@/components/sync/SyncTimeline";
import { ConflictResolution } from "@/lib/db/conflict";
import type { ConflictInfo, SyncQueueItem } from "@/lib/db/types";
import { removeSyncQueueItem, updateSyncQueueItem } from "@/lib/db/changes";
import { createLogger } from "@ict/shared";

const logger = createLogger({ module: "SyncPage" });

type TabType = "success" | "pending";

export function SyncPage() {
  const navigate = useNavigate();
  const { isOffline } = useNetworkStatus();
  const {
    isSyncing,
    progress,
    total,
    lastSyncResult,
    error,
    sync,
    refreshPendingCount,
    getQueue,
    cleanupOldCompleted,
  } = useSyncQueue();

  const [queue, setQueue] = useState<SyncQueueItem[]>([]);
  const [conflicts, setConflicts] = useState<ConflictInfo[]>([]);
  const [currentConflict, setCurrentConflict] = useState<ConflictInfo | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("pending");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  useEffect(() => {
    // 72시간 경과한 완료 항목 자동 정리 후 큐 로드
    cleanupOldCompleted().then(() => {
      refreshPendingCount();
      loadQueue();
    });
  }, [refreshPendingCount, cleanupOldCompleted]);

  async function loadQueue() {
    const items = await getQueue();
    setQueue(items);

    // 대기/실패 항목이 있으면 대기 탭, 없으면 성공 탭 (체크리스트 사진은 목록에서 제외되므로 판단에서도 제외)
    const hasPendingItems = items.some(
      (item) => item.status !== "completed" && !isChecklistPhoto(item)
    );
    if (hasPendingItems) {
      setActiveTab("pending");
    } else {
      setActiveTab("success");
    }
  }

  const handleSync = async () => {
    const result = await sync();
    await loadQueue();

    if (result && result.conflicts && result.conflicts.length > 0) {
      setConflicts(result.conflicts);
      setCurrentConflict(result.conflicts[0]);
    }
  };

  const handleResolveConflict = (resolution: ConflictResolution) => {
    if (!currentConflict) return;

    logger.debug(`Conflict resolved with: ${resolution}`);

    const remainingConflicts = conflicts.filter((c) => c !== currentConflict);
    setConflicts(remainingConflicts);

    if (remainingConflicts.length > 0) {
      setCurrentConflict(remainingConflicts[0]);
    } else {
      setCurrentConflict(null);
      handleSync();
    }
  };

  const handleCancelConflict = () => {
    setCurrentConflict(null);
  };

  // 개별 재시도
  const handleRetryItem = async (itemId: string) => {
    await updateSyncQueueItem(itemId, { status: "pending", retryCount: 0, lastError: undefined });
    await sync();
    await loadQueue();
    await refreshPendingCount();
  };

  // 개별 삭제 확인
  const handleDeleteItem = (itemId: string) => {
    setItemToDelete(itemId);
    setShowDeleteDialog(true);
  };

  // 개별 삭제 실행
  const confirmDeleteItem = async () => {
    if (itemToDelete) {
      await removeSyncQueueItem(itemToDelete);
      await loadQueue();
      await refreshPendingCount();
    }
    setShowDeleteDialog(false);
    setItemToDelete(null);
  };

  const progressPercent = total > 0 ? Math.round((progress / total) * 100) : 0;

  // 점검항목 사진은 "점검항목 수정"에 포함되므로 목록에서 제외
  const isChecklistPhoto = (item: SyncQueueItem) => item.type === "photo" && item.payload.checkId;

  // 탭별 항목 필터링 (점검항목 사진 제외) + 최신순 정렬
  const pendingItems = useMemo(
    () =>
      queue
        .filter((item) => item.status !== "completed" && !isChecklistPhoto(item))
        .sort((a, b) => b.createdAt - a.createdAt),
    [queue]
  );
  const successItems = useMemo(
    () =>
      queue
        .filter((item) => item.status === "completed" && !isChecklistPhoto(item))
        .sort((a, b) => (b.syncedAt ?? b.createdAt) - (a.syncedAt ?? a.createdAt)),
    [queue]
  );

  // 성공 탭: 이름 해석
  const syncItemNames = useSyncItemNames(successItems);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* 헤더 */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="flex items-center justify-between px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full active:bg-slate-100 transition-colors"
          >
            <svg
              className="h-6 w-6 text-slate-900"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-[17px] font-bold text-slate-900">동기화</h1>
          <div className="w-10" />
        </div>
      </div>

      <div className="px-4 pb-8">
        {/* 오프라인 상태 */}
        {isOffline && (
          <div className="mt-4 flex items-center gap-3 rounded-2xl bg-red-50 border border-red-200 px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-red-100">
              <svg className="h-5 w-5 text-red-600" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 20h.01M8.53 16.11a6 6 0 016.95 0M5.64 12.45a10 10 0 0112.72 0M2.1 8.82a14 14 0 0119.8 0"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  opacity="0.3"
                />
                <path
                  d="M4 4l16 16"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-[15px] font-semibold text-red-900">오프라인 상태</p>
              <p className="text-[13px] text-red-700">연결 후 동기화하세요</p>
            </div>
          </div>
        )}

        {/* 진행률 */}
        {isSyncing && (
          <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[15px] font-medium text-slate-900">진행 중</span>
              <span className="text-[15px] font-bold text-blue-500">{progressPercent}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-blue-500 transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <p className="mt-2 text-[13px] text-slate-500">
              {progress}/{total} 완료
            </p>
          </div>
        )}

        {/* 결과 카드 */}
        {lastSyncResult && !isSyncing && (
          <div
            className={`mt-4 overflow-hidden rounded-2xl p-4 border ${
              lastSyncResult.success
                ? "bg-emerald-50 border-emerald-200"
                : "bg-red-50 border-red-200"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-full ${
                  lastSyncResult.success ? "bg-emerald-100" : "bg-red-100"
                }`}
              >
                {lastSyncResult.success ? (
                  <svg
                    className="h-5 w-5 text-emerald-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-5 w-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </div>
              <div>
                <p
                  className={`text-[15px] font-semibold ${
                    lastSyncResult.success ? "text-emerald-900" : "text-red-900"
                  }`}
                >
                  {lastSyncResult.success ? "동기화 완료" : "일부 실패"}
                </p>
                <p
                  className={`text-[13px] ${
                    lastSyncResult.success ? "text-emerald-700" : "text-red-700"
                  }`}
                >
                  성공 {lastSyncResult.syncedCount} · 실패 {lastSyncResult.failedCount}
                  {lastSyncResult.conflictCount > 0 && ` · 충돌 ${lastSyncResult.conflictCount}`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 충돌 알림 */}
        {conflicts.length > 0 && !currentConflict && (
          <div className="mt-4 overflow-hidden rounded-2xl bg-amber-50 border border-amber-200 p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100">
                <svg
                  className="h-5 w-5 text-amber-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-[15px] font-semibold text-amber-900">
                  충돌 {conflicts.length}건
                </p>
                <p className="text-[13px] text-amber-700">확인이 필요해요</p>
              </div>
              <button
                onClick={() => setCurrentConflict(conflicts[0])}
                className="rounded-xl bg-amber-500 px-4 py-2 text-[14px] font-semibold text-white active:bg-amber-600"
              >
                확인
              </button>
            </div>
          </div>
        )}

        {/* 에러 */}
        {error && (
          <div className="mt-4 rounded-2xl bg-red-50 border border-red-200 p-4">
            <p className="text-[14px] text-red-900">{error}</p>
          </div>
        )}

        {/* 탭 섹션 */}
        <div className="mt-6">
          {/* 탭 버튼 */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setActiveTab("pending")}
              className={`flex-1 relative py-3 px-4 rounded-xl font-semibold text-[15px] transition-all ${
                activeTab === "pending"
                  ? "bg-red-500 text-white shadow-sm"
                  : "bg-white text-slate-600 border border-slate-200"
              }`}
            >
              <span>대기</span>
              {pendingItems.length > 0 && (
                <span
                  className={`ml-2 inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-[12px] font-bold ${
                    activeTab === "pending"
                      ? "bg-red-600 text-red-50"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {pendingItems.length}
                </span>
              )}
            </button>

            <button
              onClick={() => setActiveTab("success")}
              className={`flex-1 relative py-3 px-4 rounded-xl font-semibold text-[15px] transition-all ${
                activeTab === "success"
                  ? "bg-slate-800 text-white shadow-sm"
                  : "bg-white text-slate-600 border border-slate-200"
              }`}
            >
              <span>성공</span>
              {successItems.length > 0 && (
                <span
                  className={`ml-2 inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-[12px] font-bold ${
                    activeTab === "success"
                      ? "bg-slate-700 text-slate-50"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {successItems.length}
                </span>
              )}
            </button>
          </div>

          {/* 탭 컨텐츠 */}
          <div>
            {/* 실패 탭 */}
            <div className={activeTab === "pending" ? "block" : "hidden"}>
              {pendingItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-2xl bg-white border border-slate-200 py-16">
                  <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
                    <svg
                      className="h-7 w-7 text-emerald-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="text-[15px] text-slate-900 font-medium">대기 중인 항목이 없어요</p>
                  <p className="text-[13px] text-slate-500 mt-1">모든 동기화가 완료되었습니다</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {pendingItems.map((item) => (
                    <SyncQueueItemCard
                      key={item.id}
                      item={item}
                      onRetry={() => handleRetryItem(item.id)}
                      onDelete={() => handleDeleteItem(item.id)}
                      showActions={true}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* 성공 탭: 시간순 타임라인 */}
            <div className={activeTab === "success" ? "block" : "hidden"}>
              {successItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-2xl bg-white border border-slate-200 py-16">
                  <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
                    <svg
                      className="h-7 w-7 text-slate-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                  </div>
                  <p className="text-[15px] text-slate-900 font-medium">성공한 항목이 없어요</p>
                  <p className="text-[13px] text-slate-500 mt-1">
                    동기화 완료 후 최대 72시간까지 기록이 유지됩니다
                  </p>
                </div>
              ) : (
                <SyncTimeline items={successItems} names={syncItemNames} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 개별 삭제 확인 다이얼로그 */}
      {showDeleteDialog && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-md mx-4 bg-white rounded-2xl shadow-xl overflow-hidden animate-slide-up">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                  <svg
                    className="h-6 w-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-[17px] font-bold text-slate-900">항목 삭제</h3>
                  <p className="text-[14px] text-slate-600 mt-0.5">이 작업은 되돌릴 수 없습니다</p>
                </div>
              </div>
              <p className="text-[14px] text-slate-700 mb-6">
                선택한 동기화 항목을 대기열에서 완전히 제거할까요?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteDialog(false)}
                  className="flex-1 py-3 px-4 rounded-xl bg-slate-100 text-slate-700 font-semibold text-[15px] active:bg-slate-200 transition-colors"
                >
                  취소
                </button>
                <button
                  onClick={confirmDeleteItem}
                  className="flex-1 py-3 px-4 rounded-xl bg-red-500 text-white font-semibold text-[15px] active:bg-red-600 transition-colors"
                >
                  삭제
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 충돌 다이얼로그 */}
      {currentConflict && (
        <ConflictDialog
          conflict={currentConflict}
          onResolve={handleResolveConflict}
          onCancel={handleCancelConflict}
        />
      )}

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .animate-slide-up {
          animation: slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>
    </div>
  );
}

export default SyncPage;
